/**
 * Base currency
 * --------------
 * The currency one set of books is kept in:
 *   - a business (own or a client's): user_businesses.base_currency when set, else the
 *     currency of the business's country, else of its owner's country;
 *   - a person (account owner, family member, personal client): the currency of their
 *     country, else of the account they belong to.
 * Country → currency comes from site_mstr_countries (name / iso2 / iso3 → currency).
 * Nothing here is tied to one country: '' is returned when no country is known, and
 * the caller decides what to show.
 *
 * Pure + unit-tested (baseCurrency.test.ts); the `countries` argument defaults to the
 * cached master so screens don't each have to load it.
 */
import { getTableData } from '../../db-viewer/data/tables';

type Row = Record<string, any>;

const norm = (v: any) => String(v == null ? '' : v).trim().toLowerCase();
const code = (v: any) => String(v == null ? '' : v).trim().toUpperCase();

function countriesOrCached(countries?: Row[]): Row[] {
  if (countries && countries.length) return countries;
  try { return getTableData('site_mstr_countries'); } catch { return []; }
}

/** Currency code for a country given by name ("Canada"), iso2 ("CA") or iso3 ("CAN"). '' if unknown. */
export function currencyForCountry(country: any, countries?: Row[]): string {
  const c = norm(country);
  if (!c) return '';
  const list = countriesOrCached(countries);
  const hit = list.find(r => norm(r.name) === c)
    || list.find(r => c.length === 2 && norm(r.iso2) === c)
    || list.find(r => c.length === 3 && norm(r.iso3) === c);
  return hit ? code(hit.currency) : '';
}

/** A person's currency: their country, else the country of the account they sit under. */
export function personBaseCurrency(userId: any, usersData: Row[], countries?: Row[]): string {
  // eslint-disable-next-line eqeqeq
  const user = usersData.find(u => u.user_id == userId);
  if (!user) return '';
  const own = currencyForCountry(user.address_country || user.country_code, countries);
  if (own) return own;
  if (user.owner_id == null || user.owner_id === '') return '';
  // eslint-disable-next-line eqeqeq
  const parent = usersData.find(u => u.user_id == user.owner_id);
  return parent ? personBaseCurrency(parent.user_id, usersData.filter(u => u !== user), countries) : '';
}

/** What a business falls back to when it has no explicit base currency. */
export function businessDefaultCurrency(business: Row | null | undefined, usersData: Row[], countries?: Row[]): string {
  if (!business) return '';
  return currencyForCountry(business.busi_address_country || business.busi_country_code, countries)
    || personBaseCurrency(business.owner_id, usersData, countries);
}

/** A business's base currency: its own setting, else the country default. */
export function businessBaseCurrency(business: Row | null | undefined, usersData: Row[], countries?: Row[]): string {
  if (!business) return '';
  return code(business.base_currency) || businessDefaultCurrency(business, usersData, countries);
}

/** Base currency for one owner entity (the `owner_type:owner_id` used across bookkeeping). */
export function entityBaseCurrency(
  ownerType: string | null | undefined,
  ownerId: any,
  usersData: Row[],
  businessesData: Row[],
  countries?: Row[],
): string {
  if (!ownerType || ownerId == null || ownerId === '') return '';
  if (ownerType === 'business' || ownerType === 'client_business') {
    // eslint-disable-next-line eqeqeq
    return businessBaseCurrency(businessesData.find(b => b.business_id == ownerId), usersData, countries);
  }
  return personBaseCurrency(ownerId, usersData, countries);
}

// Shown first in a currency picker (after the base currency) — the ones met most often.
const COMMON_CURRENCIES = ['CAD', 'USD', 'EUR', 'GBP', 'INR', 'AUD', 'NZD', 'CHF', 'JPY', 'CNY', 'HKD', 'SGD', 'AED', 'MXN'];

/**
 * Options for a currency picker: the base (default) currency first, then the value
 * already chosen, then the common ones, then every other currency in the countries
 * master — each once, 3-letter codes only.
 */
export function currencyOptions(base: string, current?: string, countries?: Row[]): string[] {
  const out: string[] = [];
  const add = (c: any) => { const v = code(c); if (/^[A-Z]{3}$/.test(v) && !out.includes(v)) out.push(v); };
  add(base);
  add(current);
  COMMON_CURRENCIES.forEach(add);
  countriesOrCached(countries).map(r => code(r.currency)).sort().forEach(add);
  return out;
}
